// Updated client/src/components/common/RegisterForm.js with product selection
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Link,
  Paper,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { Field, Form, Formik } from "formik";
import { useContext, useEffect, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { AuthContext } from "../../contexts/AuthContext";
import { register } from "../../services/authService";
import { getProducts } from "../../services/productService";

// Validation schema
const validationSchema = Yup.object({
  email: Yup.string()
    .email("Enter a valid email")
    .required("Email is required"),
  password: Yup.string()
    .min(8, "Password must be at least 8 characters")
    .required("Password is required"),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("password"), null], "Passwords must match")
    .required("Confirm Password is required"),
  productIds: Yup.array()
    .of(Yup.number())
    .min(1, "Please select at least one product"),
});

const RegisterForm = ({ isAdminCreating = false, onUserCreated = null }) => {
  const navigate = useNavigate();
  const { login: authLogin, user } = useContext(AuthContext);
  const [error, setError] = useState("");
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);

  useEffect(() => {
    // Only load products if admin is creating user or if it's regular registration
    if (isAdminCreating || !user) {
      fetchProducts();
    } else {
      setLoadingProducts(false);
    }
  }, [isAdminCreating, user]);

  const fetchProducts = async () => {
    try {
      setLoadingProducts(true);
      const response = await getProducts();
      setProducts(response || []);
    } catch (error) {
      console.error("Error fetching products:", error);
      setError("Failed to load products. Please try again.");
    } finally {
      setLoadingProducts(false);
    }
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      // Prepare registration data
      const registrationData = {
        email: values.email,
        password: values.password,
        productIds: values.productIds || [],
      };

      // If admin is creating user, add role
      if (isAdminCreating) {
        registrationData.role = values.role || "user";

        // Send the richer `products` shape so the license-server flag is set at
        // creation time rather than only on a follow-up edit. Public
        // self-registration keeps the legacy productIds payload — the backend
        // treats those as standard (non-license-server) assignments.
        registrationData.products = (values.productIds || []).map((id) => ({
          productId: id,
          isLicenseServer: (values.licenseServerIds || []).includes(id),
        }));
        delete registrationData.productIds;
      }

      const response = await register(registrationData);

      if (isAdminCreating && onUserCreated) {
        // If admin is creating user, call callback
        onUserCreated(response.user);
      } else {
        // If regular registration, log in the user
        authLogin(response.user, response.token);
        navigate("/dashboard");
      }
    } catch (error) {
      // Handle error response from the server
      if (error && typeof error === "object") {
        setError(error.message || "Registration failed. Please try again.");
      } else {
        setError("Registration failed. Please try again.");
      }
      console.error("Registration error:", error);
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingProducts) {
    return (
      <Box sx={{ width: "100%" }}>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="50vh"
        >
          <CircularProgress />
          <Typography variant="body1" sx={{ ml: 2 }}>
            Loading products...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      <Paper
        elevation={isAdminCreating ? 0 : 3}
        sx={{
          p: isAdminCreating ? 3 : 4,
          mt: isAdminCreating ? 0 : 8,
          ...(isAdminCreating && {
            boxShadow: "none",
            border: "none",
            bgcolor: "transparent",
          }),
        }}
      >
        {!isAdminCreating && (
          <Typography variant="h4" component="h1" gutterBottom align="center">
            Register
          </Typography>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Formik
          initialValues={{
            email: "",
            password: "",
            confirmPassword: "",
            productIds: [],
            licenseServerIds: [],
            role: "user",
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting, errors, touched, values, setFieldValue }) => (
            <Form>
              <Box mb={3}>
                <Field
                  as={TextField}
                  fullWidth
                  id="email"
                  name="email"
                  label="Email"
                  error={touched.email && Boolean(errors.email)}
                  helperText={touched.email && errors.email}
                />
              </Box>

              <Box mb={3}>
                <Field
                  as={TextField}
                  fullWidth
                  id="password"
                  name="password"
                  label="Password"
                  type="password"
                  error={touched.password && Boolean(errors.password)}
                  helperText={touched.password && errors.password}
                />
              </Box>

              <Box mb={3}>
                <Field
                  as={TextField}
                  fullWidth
                  id="confirmPassword"
                  name="confirmPassword"
                  label="Confirm Password"
                  type="password"
                  error={
                    touched.confirmPassword && Boolean(errors.confirmPassword)
                  }
                  helperText={touched.confirmPassword && errors.confirmPassword}
                />
              </Box>

              {isAdminCreating && (
                <Box mb={3}>
                  <FormControl fullWidth>
                    <FormLabel component="legend">Role</FormLabel>
                    <Field
                      as={TextField}
                      select
                      name="role"
                      value={values.role}
                      onChange={(e) => setFieldValue("role", e.target.value)}
                      SelectProps={{
                        native: true,
                      }}
                    >
                      <option value="user">User</option>
                      <option value="admin">Admin</option>
                    </Field>
                  </FormControl>
                </Box>
              )}

              <Card
                elevation={0}
                sx={{ mb: 3, maxHeight: 400, overflow: "auto" }}
              >
                <CardContent>
                  <FormControl component="fieldset" fullWidth>
                    <FormLabel component="legend" sx={{ mb: 2 }}>
                      Select Products Access
                      <Typography
                        variant="caption"
                        display="block"
                        color="text.secondary"
                      >
                        Choose which products this user will have access to
                      </Typography>
                    </FormLabel>
                    <FormGroup>
                      {products.map((product) => (
                        <FormControlLabel
                          key={product.id}
                          sx={{
                            alignItems: "flex-start",
                            mb: 2,
                            "& .MuiFormControlLabel-label": {
                              width: "100%",
                            },
                          }}
                          control={
                            <Checkbox
                              checked={values.productIds.includes(product.id)}
                              onChange={(e) => {
                                const productIds = [...values.productIds];
                                if (e.target.checked) {
                                  productIds.push(product.id);
                                } else {
                                  const index = productIds.indexOf(product.id);
                                  if (index > -1) {
                                    productIds.splice(index, 1);
                                  }
                                  // Deselecting a product clears its
                                  // license-server flag so a stale flag can't
                                  // be submitted for an unassigned product.
                                  setFieldValue(
                                    "licenseServerIds",
                                    (values.licenseServerIds || []).filter(
                                      (id) => id !== product.id,
                                    ),
                                  );
                                }
                                setFieldValue("productIds", productIds);
                              }}
                              color="primary"
                              sx={{ mt: 0.5 }}
                            />
                          }
                          label={
                            <Box sx={{ width: "100%" }}>
                              <Typography variant="body1" fontWeight="medium">
                                {product.name}
                              </Typography>
                              {product.description && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{
                                    display: "block",
                                    lineHeight: 1.4,
                                    mt: 0.5,
                                    wordBreak: "break-word",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    display: "-webkit-box",
                                    WebkitLineClamp: 3,
                                    WebkitBoxOrient: "vertical",
                                  }}
                                  title={product.description}
                                >
                                  {product.description}
                                </Typography>
                              )}
                              {product.requires_license && (
                                <Chip
                                  size="small"
                                  label="Requires License"
                                  color="warning"
                                  sx={{ mt: 1, height: 20 }}
                                />
                              )}
                              {isAdminCreating &&
                                values.productIds.includes(product.id) && (
                                  <FormControlLabel
                                    sx={{ display: "block", mt: 0.5 }}
                                    control={
                                      <Switch
                                        size="small"
                                        checked={(
                                          values.licenseServerIds || []
                                        ).includes(product.id)}
                                        onChange={(e) => {
                                          const licenseServerIds = [
                                            ...(values.licenseServerIds || []),
                                          ];
                                          if (e.target.checked) {
                                            licenseServerIds.push(product.id);
                                          } else {
                                            const index =
                                              licenseServerIds.indexOf(
                                                product.id,
                                              );
                                            if (index > -1) {
                                              licenseServerIds.splice(index, 1);
                                            }
                                          }
                                          setFieldValue(
                                            "licenseServerIds",
                                            licenseServerIds,
                                          );
                                        }}
                                      />
                                    }
                                    label={
                                      <Typography variant="caption">
                                        License server user
                                      </Typography>
                                    }
                                  />
                                )}
                            </Box>
                          }
                        />
                      ))}
                    </FormGroup>
                    {touched.productIds && errors.productIds && (
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{ mt: 1 }}
                      >
                        {errors.productIds}
                      </Typography>
                    )}
                  </FormControl>
                </CardContent>
              </Card>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                disabled={isSubmitting}
                sx={{ mb: 2 }}
              >
                {isSubmitting
                  ? "Creating..."
                  : isAdminCreating
                    ? "Create User"
                    : "Register"}
              </Button>

              {!isAdminCreating && (
                <Box mt={2} textAlign="center">
                  <Typography variant="body2">
                    Already have an account?{" "}
                    <Link component={RouterLink} to="/login">
                      Login
                    </Link>
                  </Typography>
                </Box>
              )}
            </Form>
          )}
        </Formik>
      </Paper>
    </Box>
  );
};

export default RegisterForm;
